import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useTransition } from 'react';

import { FunnelXIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetPortal,
  SheetTitle,
  Text,
} from '@/_components';
import { useBoolean } from '@/_shared';

import { FilterBadge } from './filter-badge';
import {
  BooleanFilter,
  DateFilterWithRadios,
  InputFilterWithRadios,
  RadioOnlyFilters,
  SelectFilterWithRadios,
} from './filters';
import type { FiltersContextValue } from './filters-context';
import { FiltersContext } from './filters-context';
import type { AdvanceFilter } from './types';

export function FiltersContextWithAccordion<T>({
  children,
  title,
  advFilters,
  defaultOpenFilters,
  filterRenderer,
  handleSubmitAdvFilters,
  handleClearAllAdvFilters,
}: PropsWithChildren<{
  title: string;
  defaultOpenFilters?: string;
  advFilters: AdvanceFilter<T>[];
  handleClearAllAdvFilters: () => void;
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
  filterRenderer: (f: AdvanceFilter<T>, handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void) => ReactNode;
}>) {
  const [sidebarOpen, setSidebarOpen] = useBoolean();
  const appliedFilters = advFilters.filter((f) => f.applied);
  const [_, startTransition] = useTransition();

  const handleClearAllFilters = useCallback(() => {
    startTransition(() => {
      handleClearAllAdvFilters();
    });
  }, [handleClearAllAdvFilters]);

  const contextValue: FiltersContextValue = {
    sidebarOpen,
    setSidebarOpen: setSidebarOpen.set,
  };

  return (
    <FiltersContext value={contextValue}>
      <Sheet
        open={sidebarOpen}
        onOpenChange={(open) => {
          setSidebarOpen.set(open);
        }}
      >
        <SheetPortal>
          <SheetContent side="right" className="flex flex-col py-6 pr-0 pl-4">
            <SheetHeader>
              <SheetTitle className="text-accent pl-2 text-left dark:text-white">{title} Filters</SheetTitle>
              <SheetDescription asChild>
                <div className="flex flex-col gap-1 pr-6">
                  {appliedFilters.map((f, i) => (
                    <section key={(f.mainKey as string) + '_' + i}>
                      <FilterBadge f={f} handleSubmitAdvFilters={handleSubmitAdvFilters} />
                    </section>
                  ))}
                </div>
              </SheetDescription>
            </SheetHeader>

            <ScrollArea className="h-full pr-4 pb-8">
              <Accordion defaultValue={defaultOpenFilters} type="single" collapsible className="w-full px-2 pb-7">
                {advFilters.map((f, i) => {
                  const { mainKey, title, subKey, fieldType } = f;
                  const key = (mainKey as string) + '.' + subKey + '.' + i;

                  let node = null;
                  if (fieldType === 'custom') {
                    node = filterRenderer(f, handleSubmitAdvFilters);
                  } else if (fieldType === 'select') {
                    node = (
                      <SelectFilterWithRadios
                        {...f}
                        handleSubmitAdvFilters={handleSubmitAdvFilters}
                        options={f.options ?? []}
                        getOptionLabel={(option) => option.name}
                      />
                    );
                  } else if (fieldType === 'radio') {
                    node = (
                      <RadioOnlyFilters
                        {...f}
                        options={f.options ?? []}
                        handleSubmitAdvFilters={handleSubmitAdvFilters}
                      />
                    );
                  } else if (fieldType === 'boolean') {
                    node = (
                      <BooleanFilter {...f} options={f.options ?? []} handleSubmitAdvFilters={handleSubmitAdvFilters} />
                    );
                  } else if (fieldType === 'date') {
                    node = <DateFilterWithRadios {...f} handleSubmitAdvFilters={handleSubmitAdvFilters} />;
                  } else {
                    node = <InputFilterWithRadios {...f} handleSubmitAdvFilters={handleSubmitAdvFilters} />;
                  }

                  return (
                    <AccordionItem
                      key={key}
                      className={twMerge(
                        'border-c-border border-b pt-2 pb-2',
                        i === advFilters.length - 1 && 'border-none'
                      )}
                      value={key}
                    >
                      <AccordionTrigger className="cursor-pointer">
                        <Text className="text-accent tex-sm dark:text-white">{title}</Text>
                      </AccordionTrigger>
                      <AccordionContent>{node}</AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </ScrollArea>

            <section className="border-c-border absolute right-0 bottom-0 left-0 border-t bg-white p-2 dark:bg-gray-950">
              <Button
                as="link"
                className="w-full justify-between"
                iconRight={FunnelXIcon}
                onClick={handleClearAllFilters}
              >
                Clear all filters
              </Button>
            </section>
          </SheetContent>
        </SheetPortal>
        {children}
      </Sheet>
    </FiltersContext>
  );
}
